import { UI_CONFIG_STORAGE_KEY } from '@app/core/config/ui-config.defaults';
import { UiConfig, UiConfigWire } from '@app/core/config/ui-config.model';
import { normalizeUiConfig } from '@app/core/config/ui-config.normalize';
import { environment } from 'src/environments/environment';

import { pareceChunkQueNoCargo } from './chunk-caido';

/**
 * Pantalla de «no se pudo iniciar».
 *
 * La pinta `main.ts` cuando el arranque rechaza, casi siempre porque el `import()`
 * de `app.config` o de `App` no se descargó. Se construye con DOM a mano a
 * propósito: Angular es justo lo que no arrancó.
 *
 * Por lo mismo trae su propia hoja de estilos, con clases prefijadas `cfa-`:
 * `styles.css` pudo no llegar (un `index.html` cacheado que pide hashes que ya no
 * existen), y la animación, el hover, el foco y `prefers-reduced-motion` no caben
 * en estilos en línea. Nada pide red: el dibujo es SVG en línea y la tipografía
 * cae en la del sistema si Rethink Sans no está.
 *
 * Distingue tres causas porque cada una se arregla distinto:
 *  - `sin-conexion`: el equipo no tiene red. Se espera y se recarga sola al volver.
 *  - `descarga`: el módulo no bajó aunque hay red (despliegue nuevo, parpadeo). Una
 *    recarga lo cura casi siempre, así que se hace sola una vez antes de mostrar nada.
 *  - `inesperado`: cualquier otra cosa. Recargar puede servir; si no, soporte.
 */

type Causa = 'sin-conexion' | 'descarga' | 'inesperado';

/** Identidad de la pantalla; también es su candado contra pintarla dos veces. */
const ID_PANTALLA = 'consiglia-fallo-arranque';
const ID_ESTILOS = 'consiglia-fallo-arranque-estilos';

/** La misma clave que usa ThemeService: sin Angular no hay a quién preguntarle. */
const CLAVE_TEMA = 'inversisa-theme';

/**
 * Antibucle de la recarga automática, con el mismo criterio que el del router
 * (`manejarErrorDeNavegacion`): un bucle se nota al instante —recarga, falla,
 * recarga—, así que pasado este rato un fallo nuevo ya es otro incidente.
 */
const CLAVE_RECARGA = 'consiglia.recarga-por-arranque';
const VENTANA_ANTIBUCLE_MS = 60_000;

/** Pausa entre «volvió la conexión» y recargar, para que la red asiente. */
const MS_ANTES_DE_RECARGAR = 900;

/** Colores de la edición que la pantalla respeta, como en BrandService. */
const COLORES_DE_MARCA: Readonly<Record<string, string>> = {
  primary: '--cfa-primario',
  'primary-content': '--cfa-primario-tinta',
  info: '--cfa-info',
  success: '--cfa-exito',
  error: '--cfa-error',
};

interface Textos {
  titulo: string;
  mensaje: string;
  accion: string;
  causa: string;
  pestana: string;
}

interface Marca {
  producto: string;
  codigo: string;
  /** Paquete de marca (`consiglia` | `match360`): elige la paleta de la capa. */
  marca: string;
  /** Custom properties listas para escribir en la capa. */
  variables: Record<string, string>;
}

/** Fila del detalle técnico; `maquina` la pinta en mono (Machine-Text Rule). */
type Fila = readonly [etiqueta: string, valor: string, maquina?: boolean];

/**
 * Último recurso si el arranque falla.
 *
 * Antes esto sólo hacía `console.error` y dejaba la pantalla en blanco: el
 * usuario no tiene la consola abierta. Reintentar el `import()` no sirve, porque
 * el navegador cachea el rechazo en su mapa de módulos; de ahí sólo se sale con
 * una carga nueva del documento.
 */
export function mostrarFalloDeArranque(error: unknown): void {
  console.error('[arranque-fallido]', error);
  if (document.getElementById(ID_PANTALLA)) return;

  const causa = diagnosticar(error);
  if (causa === 'descarga' && recargaAutomaticaPermitida()) {
    location.reload();
    return;
  }

  pintar(causa, error);
}

/** El arranque terminó bien: la próxima caída de descarga merece su recarga. */
export function marcarArranqueCompletado(): void {
  try {
    sessionStorage.removeItem(CLAVE_RECARGA);
  } catch {
    // Sin sessionStorage no hubo marca que limpiar.
  }
}

function diagnosticar(error: unknown): Causa {
  // `onLine === false` es fiable (el equipo no tiene red); `true` no lo es —hay
  // red local sin salida—, por eso sólo se cree en un sentido.
  if (navigator.onLine === false) return 'sin-conexion';
  return pareceChunkQueNoCargo(error) ? 'descarga' : 'inesperado';
}

/**
 * ¿Se puede recargar sola? Deja anotado el intento al pasar, así que la primera
 * llamada devuelve `true` y la siguiente, dentro de la ventana, `false`.
 *
 * Sin `sessionStorage` se responde `false`: sin memoria no hay forma de frenar un
 * bucle, y una pantalla con un botón es mejor que recargar en círculo.
 */
function recargaAutomaticaPermitida(): boolean {
  try {
    const previa = Number(sessionStorage.getItem(CLAVE_RECARGA));
    if (previa > 0 && Date.now() - previa < VENTANA_ANTIBUCLE_MS) return false;
    sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function textosPara(causa: Causa, producto: string): Textos {
  switch (causa) {
    case 'sin-conexion':
      return {
        titulo: 'Sin conexión a internet',
        mensaje: `${producto} necesita conexión para abrir. En cuanto vuelva, la página se recarga sola.`,
        accion: 'Reintentar ahora',
        causa: 'Sin conexión',
        pestana: `Sin conexión · ${producto}`,
      };
    case 'descarga':
      return {
        titulo: `No se pudo iniciar ${producto}`,
        mensaje:
          'Una parte de la aplicación no terminó de descargarse, algo normal cuando se publica una versión nueva o la red parpadea. Recargar trae la versión más reciente.',
        accion: 'Recargar',
        causa: 'Descarga incompleta',
        pestana: `No se pudo iniciar · ${producto}`,
      };
    case 'inesperado':
      return {
        titulo: `No se pudo iniciar ${producto}`,
        mensaje:
          'Algo falló mientras la aplicación arrancaba. Recarga para intentarlo de nuevo; si se repite, copia el detalle técnico y envíalo a soporte.',
        accion: 'Recargar',
        causa: 'Error al arrancar',
        pestana: `No se pudo iniciar · ${producto}`,
      };
  }
}

function pintar(causa: Causa, error: unknown): void {
  const marca = leerMarca();
  const textos = textosPara(causa, marca.producto);
  const oscuro = temaOscuro();

  instalarEstilos();
  // Si `styles.css` sí llegó, que el documento entero hable el mismo tema.
  document.documentElement.setAttribute(
    'data-theme',
    oscuro ? 'inversisa-dark' : 'inversisa-light',
  );
  document.title = textos.pestana;

  const capa = crear('div', { id: ID_PANTALLA, class: 'cfa' });
  capa.dataset['tema'] = oscuro ? 'oscuro' : 'claro';
  capa.dataset['marca'] = marca.marca;
  capa.dataset['causa'] = causa;
  for (const [variable, valor] of Object.entries(marca.variables)) {
    capa.style.setProperty(variable, valor);
  }

  const columna = crear('main', {
    class: 'cfa-columna',
    'aria-labelledby': 'cfa-titulo',
  });
  columna.insertAdjacentHTML('beforeend', DIBUJO);

  const aviso = crear('div', { class: 'cfa-texto', role: 'alert' });
  const titulo = crear(
    'h1',
    { id: 'cfa-titulo', class: 'cfa-titulo' },
    textos.titulo,
  );
  const mensaje = crear('p', { class: 'cfa-mensaje' }, textos.mensaje);
  aviso.append(titulo, mensaje);
  columna.append(aviso);

  const estado = crear('p', { class: 'cfa-estado', role: 'status' });
  const palabraEstado = crear('span', {}, 'Esperando conexión…');
  estado.append(crear('span', { class: 'cfa-punto', 'aria-hidden': 'true' }));
  estado.append(palabraEstado);
  if (causa === 'sin-conexion') columna.append(estado);

  const boton = crear('button', { type: 'button', class: 'cfa-boton' });
  boton.insertAdjacentHTML('beforeend', icono('cfa-icono-recargar', RECARGAR));
  const etiquetaBoton = crear('span', {}, textos.accion);
  boton.append(etiquetaBoton);
  const acciones = crear('div', { class: 'cfa-acciones' });
  acciones.append(boton);
  columna.append(acciones);

  const { resumen, traza } = describirError(error);
  const filas: Fila[] = [
    ['Causa', textos.causa],
    ['Error', recortar(resumen, 400), true],
    ['Edición', `${marca.producto} · ${marca.codigo}`],
    ['Versión', environment.appVersion || '—', true],
    ['Ruta', rutaSegura(), true],
    ['Hora', horaBogota()],
  ];
  const copia = [
    `${marca.producto}: no se pudo iniciar`,
    ...filas.map(([etiqueta, valor]) => `${etiqueta}: ${valor}`),
    `Navegador: ${navigator.userAgent}`,
    ...(traza ? ['Traza:', traza] : []),
  ].join('\n');
  columna.append(crearDetalle(filas, copia));

  capa.append(columna);
  document.body.append(capa);

  const clavija = capa.querySelector<SVGGElement>('.cfa-clavija');

  const recargar = (etiqueta: string): void => {
    boton.setAttribute('aria-busy', 'true');
    etiquetaBoton.textContent = etiqueta;
    location.reload();
  };

  let vueltaAlEstado: ReturnType<typeof setTimeout> | undefined;
  boton.addEventListener('click', () => {
    if (boton.getAttribute('aria-busy') === 'true') return;

    // Recargar sin red cambia esta pantalla por la del navegador, que ya no se
    // recupera sola. Mejor decirlo y dejar que la clavija lo intente otra vez.
    if (causa === 'sin-conexion' && navigator.onLine === false) {
      palabraEstado.textContent =
        'Sigue sin conexión. Se recargará sola cuando vuelva.';
      if (clavija) reiniciarClase(clavija, 'cfa-empujon');
      clearTimeout(vueltaAlEstado);
      vueltaAlEstado = setTimeout(() => {
        palabraEstado.textContent = 'Esperando conexión…';
      }, 3500);
      return;
    }

    recargar('Recargando…');
  });

  clavija?.addEventListener('animationend', (evento) => {
    if (evento.animationName === 'cfa-intentar-ya') {
      clavija.classList.remove('cfa-empujon');
    }
  });

  if (causa !== 'sin-conexion') return;

  window.addEventListener(
    'online',
    () => {
      clearTimeout(vueltaAlEstado);
      if (clavija) {
        // La clavija entra desde donde la pille el bucle, no desde el reposo.
        clavija.style.setProperty(
          '--cfa-desde',
          `${desplazamientoX(clavija)}px`,
        );
        clavija.classList.remove('cfa-empujon');
      }
      capa.classList.add('cfa-conectado');
      // Todo lo visible cambia a la vez: un titular que siguiera diciendo «Sin
      // conexión» junto a una clavija enchufada se contradice. El texto nuevo es
      // más corto; sin fijar la altura, la columna se re-centraría y el dibujo
      // bajaría justo mientras la clavija entra.
      aviso.style.minHeight = `${aviso.getBoundingClientRect().height}px`;
      titulo.textContent = 'Conexión recuperada';
      mensaje.textContent = `${marca.producto} se está abriendo de nuevo.`;
      document.title = `Conexión recuperada · ${marca.producto}`;
      palabraEstado.textContent = 'En línea';
      boton.setAttribute('aria-busy', 'true');
      etiquetaBoton.textContent = 'Abriendo…';
      setTimeout(() => location.reload(), MS_ANTES_DE_RECARGAR);
    },
    { once: true },
  );
}

function crearDetalle(filas: readonly Fila[], copia: string): HTMLElement {
  const detalle = crear('details', { class: 'cfa-detalle' });

  const resumen = crear('summary', { class: 'cfa-resumen' });
  resumen.insertAdjacentHTML('beforeend', icono('', CHEVRON));
  resumen.append('Detalle técnico');

  const lista = crear('dl', { class: 'cfa-lista' });
  for (const [etiqueta, valor, maquina] of filas) {
    lista.append(
      crear('dt', {}, etiqueta),
      crear('dd', maquina ? { class: 'cfa-maquina' } : {}, valor),
    );
  }

  const copiar = crear('button', { type: 'button', class: 'cfa-copiar' });
  copiar.insertAdjacentHTML(
    'beforeend',
    icono('cfa-icono-copiar', COPIAR) + icono('cfa-icono-listo', LISTO),
  );
  const etiquetaCopiar = crear('span', {}, 'Copiar');
  copiar.append(etiquetaCopiar);

  const anuncio = crear('span', { class: 'cfa-oculto', 'aria-live': 'polite' });

  let vuelta: ReturnType<typeof setTimeout> | undefined;
  copiar.addEventListener('click', async () => {
    const copiado = await copiarAlPortapapeles(copia);
    clearTimeout(vuelta);

    if (copiado) {
      copiar.dataset['copiado'] = '';
      etiquetaCopiar.textContent = 'Copiado';
      anuncio.textContent = 'Detalle técnico copiado al portapapeles.';
    } else {
      // Sin portapapeles (HTTP plano, permiso negado) queda seleccionado para
      // que el atajo de siempre haga el resto.
      const rango = document.createRange();
      rango.selectNodeContents(lista);
      getSelection()?.removeAllRanges();
      getSelection()?.addRange(rango);
      const atajo = /Mac|iPhone|iPad/.test(navigator.userAgent)
        ? '⌘C'
        : 'Ctrl+C';
      etiquetaCopiar.textContent = `Seleccionado: pulsa ${atajo}`;
      anuncio.textContent = `No se pudo copiar solo. El detalle quedó seleccionado; cópialo con ${atajo}.`;
    }

    vuelta = setTimeout(() => {
      delete copiar.dataset['copiado'];
      etiquetaCopiar.textContent = 'Copiar';
    }, 2400);
  });

  const pie = crear('div', { class: 'cfa-pie' });
  pie.append(copiar);

  const caja = crear('div', { class: 'cfa-caja' });
  caja.append(lista, pie, anuncio);
  const pliegue = crear('div', { class: 'cfa-pliegue' });
  pliegue.append(caja);
  detalle.append(resumen, pliegue);
  return detalle;
}

async function copiarAlPortapapeles(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

/** Nombre, código y colores de la edición, del último `ui-config` cacheado. */
function leerMarca(): Marca {
  let config: UiConfig;
  try {
    const crudo = localStorage.getItem(UI_CONFIG_STORAGE_KEY);
    config = normalizeUiConfig(
      crudo ? (JSON.parse(crudo) as UiConfigWire) : null,
    );
  } catch {
    config = normalizeUiConfig(null);
  }

  const variables: Record<string, string> = {};
  for (const [clave, variable] of Object.entries(COLORES_DE_MARCA)) {
    const valor = config.branding.colores[clave];
    if (valor && CSS.supports('color', valor)) variables[variable] = valor;
  }

  const fuente = config.branding.fuente.replace(/["'\\;{}]/g, '').trim();
  if (fuente) variables['--cfa-fuente-marca'] = `'${fuente}'`;

  return {
    producto: config.edicion.nombreProducto,
    codigo: config.edicion.codigo,
    marca: config.branding.marca,
    variables,
  };
}

function temaOscuro(): boolean {
  try {
    const guardado = localStorage.getItem(CLAVE_TEMA);
    if (guardado === 'inversisa-dark') return true;
    if (guardado === 'inversisa-light') return false;
  } catch {
    // Sin localStorage manda la preferencia del sistema, como en ThemeService.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function describirError(error: unknown): { resumen: string; traza: string } {
  if (error instanceof Error) {
    const traza = (error.stack ?? '').split('\n').slice(0, 12).join('\n');
    return { resumen: `${error.name}: ${error.message}`, traza };
  }
  if (typeof error === 'string') return { resumen: error, traza: '' };

  try {
    return { resumen: JSON.stringify(error) ?? String(error), traza: '' };
  } catch {
    return { resumen: String(error), traza: '' };
  }
}

/**
 * La ruta sin query ni hash, con los segmentos largos tapados: `/recaudos/:token`
 * lleva un JWT en la URL y el retorno de SSO trae `?code`, y nada de eso debe
 * terminar pegado en un chat de soporte.
 */
function rutaSegura(): string {
  const ruta = location.pathname
    .split('/')
    .map((segmento) => (segmento.length > 32 ? '…' : segmento))
    .join('/');
  return ruta || '/';
}

function horaBogota(): string {
  try {
    const hora = new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      dateStyle: 'medium',
      timeStyle: 'medium',
    }).format(new Date());
    return `${hora} (Bogotá)`;
  } catch {
    return new Date().toISOString();
  }
}

function recortar(texto: string, maximo: number): string {
  return texto.length > maximo ? `${texto.slice(0, maximo - 1)}…` : texto;
}

function desplazamientoX(elemento: Element): number {
  try {
    return new DOMMatrixReadOnly(getComputedStyle(elemento).transform).m41;
  } catch {
    return 0;
  }
}

/** Quita y vuelve a poner una clase para que su animación arranque de cero. */
function reiniciarClase(elemento: Element, clase: string): void {
  elemento.classList.remove(clase);
  void elemento.getBoundingClientRect();
  elemento.classList.add(clase);
}

function crear<K extends keyof HTMLElementTagNameMap>(
  etiqueta: K,
  atributos: Record<string, string> = {},
  texto?: string,
): HTMLElementTagNameMap[K] {
  const elemento = document.createElement(etiqueta);
  for (const [nombre, valor] of Object.entries(atributos)) {
    elemento.setAttribute(nombre, valor);
  }
  if (texto !== undefined) elemento.textContent = texto;
  return elemento;
}

function instalarEstilos(): void {
  if (document.getElementById(ID_ESTILOS)) return;
  const hoja = crear('style', { id: ID_ESTILOS });
  hoja.textContent = ESTILOS;
  document.head.append(hoja);
}

// --- Dibujo e iconos -------------------------------------------------------
// Cadenas fijas: nada de lo que llega en tiempo de ejecución pasa por aquí.

/**
 * Clavija y toma, de perfil. En reposo están separadas; `translateX(30px)` sobre
 * `.cfa-clavija` la deja enchufada (las patas quedan tapadas por la toma, que va
 * después y tiene relleno). Los cables salen del viewBox para que moverla nunca
 * deje ver dónde empiezan.
 */
const DIBUJO = `<svg class="cfa-dibujo" viewBox="0 0 168 64" width="192" height="73" aria-hidden="true" focusable="false">
<g class="cfa-clavija">
<path class="cfa-cable" d="M-48 44H-6C10 44 16 32 30 32H38"/>
<rect class="cfa-cuerpo" x="38" y="27" width="6" height="10" rx="2"/>
<rect class="cfa-cuerpo" x="44" y="20" width="26" height="24" rx="5"/>
<path class="cfa-patas" d="M70 27H82M70 37H82"/>
</g>
<g class="cfa-chispas">
<path pathLength="1" d="M91 25V16"/>
<path pathLength="1" d="M94 26.8L97.8 20.2"/>
<path pathLength="1" d="M88 26.8L84.2 20.2"/>
<path pathLength="1" d="M91 39V48"/>
<path pathLength="1" d="M94 37.2L97.8 43.8"/>
<path pathLength="1" d="M88 37.2L84.2 43.8"/>
</g>
<g class="cfa-toma">
<path class="cfa-cable" d="M126 32H134C148 32 152 20 166 20H216"/>
<rect class="cfa-cuerpo" x="100" y="18" width="26" height="28" rx="5"/>
<path class="cfa-ranuras" d="M104 27H109M104 37H109"/>
</g>
</svg>`;

// Trazos de Lucide (rotate-cw, chevron-right, copy, check).
const RECARGAR =
  '<path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>';
const CHEVRON = '<path d="m9 18 6-6-6-6"/>';
const COPIAR =
  '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>';
const LISTO = '<path d="M20 6 9 17l-5-5"/>';

function icono(clase: string, trazos: string): string {
  return `<svg class="cfa-icono ${clase}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${trazos}</svg>`;
}

// --- Estilos ---------------------------------------------------------------
// Los tokens replican `inversisa-light` / `inversisa-dark` de `styles.css`; la
// edición pisa primario, info, éxito, error y fuente desde la capa (ver
// `leerMarca`). Los trazos mezclan con el fondo en vez de usar transparencia
// para que el borde de una figura rellena no salga a dos tonos.

const ESTILOS = `
.cfa {
  --cfa-fondo: #f6f6f6;
  --cfa-tinta: #0d0d0d;
  --cfa-relleno: #e7e7e7;
  --cfa-linea: #d1d1d1;
  --cfa-primario: #ffe601;
  --cfa-primario-tinta: #0d0d0d;
  --cfa-error: #ff5154;
  --cfa-info: #0064a4;
  --cfa-exito: #15d26c;
  --cfa-curva: cubic-bezier(0.16, 1, 0.3, 1);
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  overflow-y: auto;
  padding: 10vh 1.5rem 14vh;
  background: var(--cfa-fondo);
  color: var(--cfa-tinta);
  font-family: var(--cfa-fuente-marca, 'Rethink Sans Variable'), 'Rethink Sans Variable', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  color-scheme: light;
  scrollbar-color: var(--cfa-linea) transparent;
  -webkit-font-smoothing: antialiased;
  interpolate-size: allow-keywords;
}
.cfa[data-tema='oscuro'] {
  --cfa-fondo: #1a1a1a;
  --cfa-tinta: #f6f6f6;
  --cfa-relleno: #2d2d2d;
  --cfa-linea: #404040;
  --cfa-error: #ef4444;
  --cfa-info: #b9e3fe;
  --cfa-exito: #22c55e;
  color-scheme: dark;
}
/* Match360: tinta y cobalto, sin amarillo (manual §6). Replica [data-brand='match360'] de styles.css. */
.cfa[data-marca='match360'] {
  --cfa-fondo: #f6f7f9;
  --cfa-tinta: #0b0e14;
  --cfa-relleno: #e8eaee;
  --cfa-linea: #d3d7de;
  --cfa-primario: #06296a;
  --cfa-primario-tinta: #ffffff;
  --cfa-error: #c42b31;
  --cfa-info: #02798a;
  --cfa-exito: #058c5a;
}
.cfa[data-marca='match360'][data-tema='oscuro'] {
  --cfa-fondo: #17191d;
  --cfa-tinta: #f4f5f7;
  --cfa-relleno: #212429;
  --cfa-linea: #363a42;
  --cfa-primario: #3fd0e3;
  --cfa-primario-tinta: #0b0e14;
  --cfa-error: #ff7276;
  --cfa-info: #a9c4f5;
  --cfa-exito: #2fe0a0;
}
.cfa *, .cfa *::before, .cfa *::after { box-sizing: border-box; }
.cfa ::selection { background: var(--cfa-tinta); color: var(--cfa-fondo); }

.cfa-columna {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 27rem;
  margin: auto;
}

.cfa-dibujo { display: block; width: 192px; max-width: 100%; height: auto; margin: 0 0 1.75rem; overflow: hidden; }
.cfa-dibujo path, .cfa-dibujo rect { fill: none; stroke-width: 1.75; stroke-linecap: round; stroke-linejoin: round; }
.cfa-dibujo .cfa-cable { stroke: color-mix(in oklab, var(--cfa-tinta) 38%, var(--cfa-fondo)); }
.cfa-dibujo .cfa-cuerpo {
  fill: var(--cfa-relleno);
  stroke: color-mix(in oklab, var(--cfa-tinta) 85%, var(--cfa-fondo));
  transition: stroke 300ms ease-out;
}
.cfa-dibujo .cfa-patas { stroke: color-mix(in oklab, var(--cfa-tinta) 70%, var(--cfa-fondo)); stroke-width: 2.25; }
.cfa-dibujo .cfa-ranuras { stroke: color-mix(in oklab, var(--cfa-tinta) 45%, var(--cfa-fondo)); }
.cfa-dibujo .cfa-chispas path { stroke: var(--cfa-error); stroke-width: 2; stroke-dasharray: 1; stroke-dashoffset: 0; }

.cfa-clavija { animation: cfa-soltar 640ms var(--cfa-curva) 200ms both; }
.cfa-toma { animation: cfa-retroceso 420ms var(--cfa-curva) 220ms both; }
.cfa-dibujo .cfa-chispas path { animation: cfa-chispa 360ms var(--cfa-curva) 250ms both; }
.cfa[data-causa='sin-conexion'] .cfa-chispas { display: none; }
.cfa[data-causa='sin-conexion'] .cfa-clavija {
  animation: cfa-soltar 640ms var(--cfa-curva) 200ms both, cfa-intentar 3.2s cubic-bezier(0.45, 0, 0.2, 1) 1.6s infinite;
}
.cfa .cfa-clavija.cfa-empujon { animation: cfa-intentar-ya 520ms cubic-bezier(0.45, 0, 0.2, 1) both; }
.cfa.cfa-conectado .cfa-clavija { animation: cfa-conectar 480ms var(--cfa-curva) both; }
.cfa.cfa-conectado .cfa-cuerpo { stroke: var(--cfa-exito); }

.cfa-texto, .cfa-estado, .cfa-acciones, .cfa-detalle { animation: cfa-aparecer 420ms var(--cfa-curva) 340ms both; }
.cfa-titulo { margin: 0; font-size: 1.5rem; font-weight: 600; line-height: 1.33; letter-spacing: -0.025em; text-wrap: balance; }
.cfa-mensaje {
  max-width: 26rem;
  margin: 0.5rem auto 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: color-mix(in oklab, var(--cfa-tinta) 68%, var(--cfa-fondo));
  text-wrap: pretty;
}

.cfa-estado {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.75rem;
  line-height: 1.33;
  color: color-mix(in oklab, var(--cfa-tinta) 72%, var(--cfa-fondo));
}
.cfa-punto { flex: none; width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--cfa-info); }
.cfa-conectado .cfa-punto { background: var(--cfa-exito); }

.cfa-acciones { margin-top: 1.5rem; }
.cfa-boton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 2.25rem;
  padding: 0.5rem 1rem;
  border: 0;
  border-radius: 0.5rem;
  background: var(--cfa-primario);
  color: var(--cfa-primario-tinta);
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  cursor: pointer;
  transition: background-color 150ms ease-out, transform 150ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.cfa-boton:hover { background: color-mix(in oklab, var(--cfa-primario) 90%, var(--cfa-primario-tinta)); }
.cfa-boton:active { transform: scale(0.98); }
.cfa-boton[aria-busy='true'] { cursor: progress; }
.cfa-boton[aria-busy='true'] .cfa-icono-recargar { animation: cfa-girar 900ms linear infinite; }
.cfa-boton:focus-visible, .cfa-copiar:focus-visible, .cfa-resumen:focus-visible { outline: 2px solid var(--cfa-tinta); outline-offset: 2px; }

.cfa-icono { display: block; flex: none; width: 1rem; height: 1rem; fill: none; stroke: currentColor; stroke-width: 1.75; stroke-linecap: round; stroke-linejoin: round; }

.cfa-detalle { width: 100%; margin-top: 2.5rem; text-align: left; }
.cfa-resumen {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: fit-content;
  margin: 0 auto 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.33;
  color: color-mix(in oklab, var(--cfa-tinta) 60%, var(--cfa-fondo));
  list-style: none;
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease-out, background-color 150ms ease-out;
}
.cfa-resumen::-webkit-details-marker { display: none; }
.cfa-resumen:hover { color: var(--cfa-tinta); background: color-mix(in oklab, var(--cfa-relleno) 70%, var(--cfa-fondo)); }
.cfa-resumen .cfa-icono { width: 0.875rem; height: 0.875rem; transition: transform 200ms ease-out; }
.cfa-detalle[open] .cfa-resumen .cfa-icono { transform: rotate(90deg); }
/* El mismo pliegue que el acordeón de «De dónde sale la diferencia»: filas
   0fr ↔ 1fr. Donde ::details-content no existe, abre sin animación. */
.cfa-detalle::details-content {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 240ms var(--cfa-curva), content-visibility 240ms allow-discrete;
}
.cfa-detalle[open]::details-content { grid-template-rows: 1fr; }
.cfa-pliegue {
  min-height: 0;
  overflow: clip;
  /* La caja termina justo en el borde del recorte, en una coordenada fraccionaria:
     sin este margen, a DPR 1 el redondeo se come su línea inferior de 1px. */
  overflow-clip-margin: 2px;
}

.cfa-caja {
  padding: 0.75rem 0.875rem;
  border: 1px solid color-mix(in oklab, var(--cfa-linea) 80%, var(--cfa-fondo));
  border-radius: 1rem;
}
.cfa-lista { display: grid; grid-template-columns: 4.25rem minmax(0, 1fr); gap: 0.375rem 0.75rem; margin: 0; }
.cfa-lista dt { font-size: 0.75rem; line-height: 1.4; color: color-mix(in oklab, var(--cfa-tinta) 58%, var(--cfa-fondo)); }
.cfa-lista dd {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: color-mix(in oklab, var(--cfa-tinta) 88%, var(--cfa-fondo));
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
}
.cfa-lista .cfa-maquina { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 11px; line-height: 1.55; }

.cfa-pie { display: flex; justify-content: flex-end; margin-top: 0.75rem; }
.cfa-copiar {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 1.75rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid color-mix(in oklab, var(--cfa-linea) 90%, var(--cfa-fondo));
  border-radius: 0.5rem;
  background: transparent;
  color: color-mix(in oklab, var(--cfa-tinta) 80%, var(--cfa-fondo));
  font: inherit;
  font-size: 0.75rem;
  line-height: 1.33;
  cursor: pointer;
  transition: background-color 150ms ease-out, color 150ms ease-out;
}
.cfa-copiar:hover { background: color-mix(in oklab, var(--cfa-relleno) 70%, var(--cfa-fondo)); color: var(--cfa-tinta); }
.cfa-copiar .cfa-icono { width: 0.875rem; height: 0.875rem; }
.cfa-copiar .cfa-icono-listo, .cfa-copiar[data-copiado] .cfa-icono-copiar { display: none; }
.cfa-copiar[data-copiado] .cfa-icono-listo { display: block; }

.cfa-oculto { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }

@media (max-width: 480px) {
  .cfa { padding: 8vh 1rem 12vh; }
  .cfa-dibujo { margin-bottom: 1.5rem; }
}

@keyframes cfa-soltar { from { transform: translateX(30px); } to { transform: none; } }
@keyframes cfa-retroceso { 0%, 100% { transform: none; } 35% { transform: translateX(3px); } }
@keyframes cfa-chispa { from { stroke-dashoffset: 1; opacity: 0; } to { stroke-dashoffset: 0; opacity: 1; } }
@keyframes cfa-intentar { 0%, 60%, 100% { transform: none; } 22% { transform: translateX(12px); } 34% { transform: translateX(9px); } }
@keyframes cfa-intentar-ya { 0%, 100% { transform: none; } 40% { transform: translateX(14px); } }
@keyframes cfa-conectar { from { transform: translateX(var(--cfa-desde, 0px)); } to { transform: translateX(30px); } }
@keyframes cfa-aparecer { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes cfa-fundir { from { opacity: 0; } to { opacity: 1; } }
@keyframes cfa-girar { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .cfa .cfa-clavija, .cfa .cfa-toma, .cfa .cfa-dibujo .cfa-chispas path, .cfa .cfa-icono-recargar { animation: none !important; }
  .cfa .cfa-texto, .cfa .cfa-estado, .cfa .cfa-acciones, .cfa .cfa-detalle { animation: cfa-fundir 200ms ease-out both; }
  .cfa.cfa-conectado .cfa-clavija { transform: translateX(30px); }
  .cfa-detalle::details-content { transition: none; }
}
`;
